"use client";

import { useEffect, useMemo, useState } from "react";
import { Sparkles } from "lucide-react";
import { useEditorContext } from "@/src/contexts/editorContext";
import { formatCountdown, formatLocalReset } from "@/src/helpers/usageTime";

function formatNumber(n) {
  if (n == null) return "0";
  return Number(n).toLocaleString("en-US");
}

export default function UsageContent({ setSelected }) {
  const { usageSummary } = useEditorContext();
  const [tickKey, setTickKey] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTickKey((k) => k + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const used = usageSummary?.tokens_used_today ?? 0;
  const limit = usageSummary?.daily_limit ?? 0;
  const tier = usageSummary?.tier ?? "basic";
  const resetAt = usageSummary?.next_reset_at_utc ?? null;
  const fraction = limit > 0 ? Math.min(1, used / limit) : 0;
  const percent = Math.round(fraction * 100);

  const countdown = useMemo(
    () => formatCountdown(resetAt),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resetAt, tickKey],
  );
  const localReset = useMemo(() => formatLocalReset(resetAt), [resetAt]);

  return (
    <div className="flex h-full flex-col pt-12">
      <div className="mb-4 flex items-center gap-2 px-2">
        <span className="qk-caps" style={{ color: "var(--qk-muted)" }}>
          daily usage
        </span>
        <div className="h-px flex-1" style={{ background: "var(--qk-rule)" }} />
      </div>

      {usageSummary ? (
        <>
          <div
            className="mx-2 rounded-xl p-4"
            style={{
              background: "var(--qk-paper-3)",
              border: "1px solid var(--qk-rule)",
            }}
          >
            <div className="flex items-baseline justify-between">
              <span className="qk-caps" style={{ color: "var(--qk-muted)" }}>
                tokens today
              </span>
              <span
                className="qk-mono"
                style={{ fontSize: 11, color: "var(--qk-muted)" }}
              >
                {tier === "pro" ? "pro plan" : "free plan"}
              </span>
            </div>

            <div className="mt-2 flex items-baseline gap-2">
              <span
                className="qk-tanker"
                style={{
                  fontSize: 28,
                  lineHeight: 1,
                  color: "var(--qk-ink)",
                }}
              >
                {formatNumber(used)}
              </span>
              <span
                className="qk-mono"
                style={{ fontSize: 12, color: "var(--qk-muted)" }}
              >
                / {formatNumber(limit)}
              </span>
            </div>

            <div
              className="mt-3 h-2 rounded-full"
              style={{
                background: "var(--qk-paper)",
                border: "1px solid var(--qk-rule)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${Math.max(percent, 1)}%`,
                  height: "100%",
                  background:
                    fraction >= 0.95
                      ? "var(--qk-clay)"
                      : "var(--qk-pink-deep)",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
            <div
              className="mt-2 qk-mono"
              style={{ fontSize: 10, color: "var(--qk-muted)" }}
            >
              {percent}% used
            </div>
          </div>

          <div
            className="mx-2 mt-3 rounded-xl p-4"
            style={{
              background: "var(--qk-paper-3)",
              border: "1px solid var(--qk-rule)",
            }}
          >
            <div className="qk-caps" style={{ color: "var(--qk-muted)" }}>
              resets in
            </div>
            {countdown ? (
              <div
                className="qk-tanker mt-2"
                style={{
                  fontSize: 24,
                  lineHeight: 1,
                  color: "var(--qk-ink)",
                }}
              >
                {String(countdown.hours).padStart(2, "0")}h{" "}
                {String(countdown.minutes).padStart(2, "0")}m
              </div>
            ) : (
              <div className="qk-tanker mt-2" style={{ fontSize: 22 }}>
                –
              </div>
            )}
            {localReset && (
              <div
                className="qk-mono mt-1"
                style={{
                  fontSize: 11,
                  color: "var(--qk-muted)",
                  letterSpacing: "0.04em",
                }}
              >
                {localReset.toLowerCase()} · 03:30 america/chicago
              </div>
            )}
          </div>

          {tier !== "pro" && (
            <button
              type="button"
              onClick={() => setSelected("manageSubscription")}
              className="qk-pill mx-2 mt-4 flex items-center justify-center gap-1.5"
              style={{
                background: "var(--qk-pink-deep)",
                color: "var(--qk-on-pink)",
                borderColor: "var(--qk-ink)",
                padding: "10px 16px",
                fontSize: 12,
                boxShadow: "3px 3px 0 var(--qk-ink)",
              }}
            >
              <Sparkles className="h-3.5 w-3.5" />
              upgrade to pro
            </button>
          )}
        </>
      ) : (
        <div
          className="mx-2 rounded-xl p-4"
          style={{
            background: "var(--qk-paper-3)",
            border: "1px solid var(--qk-rule)",
            color: "var(--qk-muted)",
            fontSize: 12,
          }}
        >
          Loading usage…
        </div>
      )}
    </div>
  );
}
